/**
 * FIRM-WIDE REQUIREMENT SETTINGS — the two things in settings/fieldRules that are not per-field:
 * the marker drawn beside a label at each requirement level, and how much has to be written to
 * excuse a blank soft-required field.
 *
 * The per-field table that used to live here is gone. It set requirement, help text, starting
 * value and greyed-out for one field at a time — every one of which Field Builder also sets, on
 * the field itself, alongside the layout and conditions it belongs with. Two screens editing the
 * same four settings through different documents is how they drift apart.
 *
 * This still loads and re-saves `rules` untouched, because adminUpdateSettings overwrites a
 * settings document whole.
 */

import { useEffect, useState } from 'react';
import { CallStatus, useCallable } from '../../lib/useCallable';
import { useDoc } from '../../lib/useFirestore';
import { REQUIREMENT } from '../../lib/fieldRules';
import {
  DEFAULT_INDICATORS,
  HIGHLIGHT_STYLES,
  HIGHLIGHT_TRIGGERS,
  INDICATOR_COLORS,
  INDICATOR_SYMBOLS,
  resolveHighlight,
  resolveIndicator,
  symbolForId,
  symbolIdFor,
} from '../../lib/requirementIndicators';
import { DEFAULT_BYPASS_REASON_RULES, resolveBypassReasonRules } from '../../lib/bypassReason';


export default function AdminRequirementSettings() {
  const { data: stored, loading } = useDoc('settings/fieldRules');
  const { run, busy, error, saved } = useCallable();
  /**
   * rules is loaded and saved back untouched. Per-field settings are edited in Field Builder
   * now, but adminUpdateSettings overwrites a settings document whole — so if this page saved
   * without carrying them, changing a marker here would wipe every field’s requirement.
   */
  const [rules, setRules] = useState(null);
  const [indicators, setIndicators] = useState(null);
  const [bypassReasons, setBypassReasons] = useState(null);

  useEffect(() => {
    if (stored && !rules) {
      setRules(structuredClone(stored.rules || {}));
      setIndicators(structuredClone(stored.indicators || DEFAULT_INDICATORS));
      setBypassReasons({
        ...resolveBypassReasonRules(stored.bypassReasons),
        includeInPdf: stored.bypassReasons?.includeInPdf !== false,
        showToApprovers: stored.bypassReasons?.showToApprovers !== false,
      });
    }
  }, [stored, rules]);

  if (loading || !rules || !bypassReasons)
    return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;

  const save = () =>
    run('adminUpdateSettings', {
      docId: 'fieldRules',
      value: { version: (stored?.version || 0) + 1, rules, indicators, bypassReasons },
    });

  const patchReason = (key, value) => setBypassReasons((prev) => ({ ...prev, [key]: value }));

  const patchIndicator = (level, key, value) =>
    setIndicators((prev) => ({ ...prev, [level]: { ...DEFAULT_INDICATORS[level], ...prev?.[level], [key]: value } }));


  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="rounded bg-firm-700 dark:bg-firm-500 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400 disabled:opacity-50"
        >
          Save
        </button>
        <CallStatus busy={busy} error={error} saved={saved} />
      </div>

      <p className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-xs text-slate-600 dark:text-slate-300">
        Firm-wide settings for requirement levels. Whether a particular field is required,
        greyed out or has a starting value is set on the field itself, in the
        <strong> Layout</strong> tab. <strong>Required</strong> blocks submission;
        <strong> soft required</strong> lets the submitter continue but forces them to record
        why the field is blank, which is written to the matter&apos;s audit log;
        <strong> optional</strong> is never validated.
      </p>

      <div className="rounded border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
        <h2 className="text-sm font-medium text-slate-700 dark:text-slate-200">
          Reasons for leaving a soft-required field blank
        </h2>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          A soft-required field can be submitted blank, but only with a written reason. These
          thresholds are what stops &ldquo;n/a&rdquo; from counting as one; a short list of
          non-answers (&ldquo;none&rdquo;, &ldquo;unknown&rdquo;, &ldquo;tbd&rdquo;, &ldquo;test&rdquo;&hellip;) is
          always rejected regardless of length. The reason is <strong>always</strong> written to the
          matter and its audit log and frozen at submission &mdash; these switches control only who
          else sees it, and are recorded on each matter as it is submitted, so changing them never
          alters a record that is already filed.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-xs">
          <label className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            Minimum characters
            <input
              type="number"
              min="1"
              max="500"
              className="w-20 rounded border border-slate-300 px-2 py-1 dark:border-slate-600"
              value={bypassReasons?.minCharacters ?? DEFAULT_BYPASS_REASON_RULES.minCharacters}
              onChange={(e) => patchReason('minCharacters', Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
            />
          </label>
          <label className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            Minimum words
            <input
              type="number"
              min="1"
              max="50"
              className="w-20 rounded border border-slate-300 px-2 py-1 dark:border-slate-600"
              value={bypassReasons?.minWords ?? DEFAULT_BYPASS_REASON_RULES.minWords}
              onChange={(e) => patchReason('minWords', Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
            />
          </label>
          <label className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <input
              type="checkbox"
              checked={bypassReasons?.includeInPdf !== false}
              onChange={(e) => patchReason('includeInPdf', e.target.checked)}
            />
            Print on the matter record PDF
          </label>
          <label className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <input
              type="checkbox"
              checked={bypassReasons?.showToApprovers !== false}
              onChange={(e) => patchReason('showToApprovers', e.target.checked)}
            />
            Show to approvers in their approval email
          </label>
        </div>
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          Example of a reason that passes at these settings:{' '}
          <em>&ldquo;Client has not provided a fee estimate yet.&rdquo;</em>
        </p>
      </div>

      <div className="rounded border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
        <h2 className="text-sm font-medium text-slate-700 dark:text-slate-200">Label markers</h2>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          How each requirement level is marked next to a field&apos;s label on the form. Hovering a
          marker always explains what it means, whichever symbol you pick.
        </p>
        <div className="mt-3 space-y-2">
          {[
            [REQUIREMENT.REQUIRED, 'Required'],
            [REQUIREMENT.SOFT_REQUIRED, 'Soft required'],
          ].map(([level, label]) => {
            const current = indicators?.[level] || DEFAULT_INDICATORS[level];
            const preview = resolveIndicator(level, indicators);
            return (
              <div key={level} className="flex flex-wrap items-center gap-3 text-xs">
                <span className="w-24 text-slate-600 dark:text-slate-300">{label}</span>
                <label className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
                  Symbol
                  <select
                    className="rounded border border-slate-300 px-2 py-1 text-xs dark:border-slate-600"
                    value={symbolIdFor(current.symbol)}
                    onChange={(e) => patchIndicator(level, 'symbol', symbolForId(e.target.value))}
                    aria-label={`${label} marker symbol`}
                  >
                    {INDICATOR_SYMBOLS.map(([id, text]) => (
                      <option key={id} value={id}>{text}</option>
                    ))}
                  </select>
                </label>
                <label className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
                  Colour
                  <select
                    className="rounded border border-slate-300 px-2 py-1 text-xs dark:border-slate-600"
                    value={current.color}
                    onChange={(e) => patchIndicator(level, 'color', e.target.value)}
                    aria-label={`${label} marker colour`}
                  >
                    {INDICATOR_COLORS.map(([id, text]) => (
                      <option key={id} value={id}>{text}</option>
                    ))}
                  </select>
                </label>
                <label className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
                  Field outline
                  <select
                    className="rounded border border-slate-300 px-2 py-1 text-xs dark:border-slate-600"
                    value={current.highlight}
                    onChange={(e) => patchIndicator(level, 'highlight', e.target.value)}
                    aria-label={`${label} field outline`}
                  >
                    {HIGHLIGHT_STYLES.map(([id, text]) => (
                      <option key={id} value={id}>{text}</option>
                    ))}
                  </select>
                </label>
                {current.highlight !== 'none' && (
                  <label className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
                    Shown
                    <select
                      className="rounded border border-slate-300 px-2 py-1 text-xs dark:border-slate-600"
                      value={current.highlightWhen}
                      onChange={(e) => patchIndicator(level, 'highlightWhen', e.target.value)}
                      aria-label={`${label} outline trigger`}
                    >
                      {HIGHLIGHT_TRIGGERS.map(([id, text]) => (
                        <option key={id} value={id}>{text}</option>
                      ))}
                    </select>
                  </label>
                )}
                <span className="flex items-center gap-1 text-slate-400 dark:text-slate-500">
                  Preview
                  <span className="rounded bg-slate-50 px-2 py-1 text-sm text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                    Field label
                    {preview && <span className={`ml-1 ${preview.className}`}>{preview.symbol}</span>}
                    {/* Shows the outline as it looks when active, whichever trigger is chosen. */}
                    <span
                      className={`mt-0.5 block rounded bg-white px-2 py-1 text-xs text-slate-400 dark:bg-slate-900 ${resolveHighlight(
                        level,
                        indicators,
                        { blank: true, invalid: true }
                      )}`.trimEnd()}
                    >
                      answer
                    </span>
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}
